/* =========================================================
   Diesel PDP colour swap (in-place, no URL change)
   Matches uk.diesel.com behaviour:
   - Clicking a swatch swaps gallery + price + title + sizes
   - URL is NOT changed (colours remain reachable via their own
     canonical URLs from PLP / search / sitemap)
   Wire the swatch buttons with data-swatch-swap + data-swatch-handle.

   IMPORTANT — why the WHOLE <product-info> is replaced:
   Dawn's add-to-cart reads the variant from input[name="id"] inside
   <product-form>, and that input is maintained by <product-info>, which
   resolves every option change against its OWN data-url / data-product-id
   (see product-info.js -> handleOptionValueChange). Swapping only the
   gallery/price/title/variant-selects/product-form left a stale
   <product-info> host still pointing at the PREVIOUS colour, so picking a
   size after a swap re-fetched the previous product's section and wrote the
   previous colour's variant id back into the form — the wrong product was
   added to cart. Replacing <product-info> wholesale keeps data-url,
   data-product-id, variant-selects, product-form and the installment form
   consistent as one unit, and re-upgrades the custom elements on insert.
   Do NOT go back to piecemeal sub-element swapping.
   ========================================================= */
(function () {
  'use strict';

  // ---- CONFIG: selectors tuned to this Diesel (Dawn-based) theme's PDP markup ----
  var SEL = {
    swatch:      '[data-swatch-swap]',          // set by color-swatches.liquid
    productInfo: 'product-info',                // the whole PDP unit (host of form + variant-selects)
  };

  var requestToken = 0; // guards against out-of-order swaps on rapid clicks

  function qa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // Fetch the rendered product section. Uses Shopify's Section Rendering API.
  function fetchSection(handle, sectionId) {
    return fetch('/products/' + handle + '?section_id=' + encodeURIComponent(sectionId)).then(function (r) {
      if (!r.ok) throw new Error('section fetch failed ' + r.status);
      return r.text();
    });
  }

  function setActive(btn) {
    // Optimistic feedback only — the swapped-in section renders the correct
    // active chip and label itself, and supersedes this.
    qa(SEL.swatch).forEach(function (b) {
      var on = b === btn;
      b.classList.toggle('is-current', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var lbl = document.querySelector('[data-swatch-current-label]');
    if (lbl) lbl.textContent = btn.getAttribute('data-swatch-label') || '';
  }

  // Mirrors Dawn's own product-swap post-processing (product-info.js).
  function preProcess(node) {
    node.querySelectorAll('.scroll-trigger').forEach(function (el) {
      el.classList.add('scroll-trigger--cancel');
    });
  }

  function postProcess() {
    if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
    if (window.ProductModel && window.ProductModel.loadShopifyXR) window.ProductModel.loadShopifyXR();
  }

  // Replace the ENTIRE <product-info> with the swapped-to colour's rendered
  // section, so the form and its variant state come from one consistent source.
  function swapViaSection(handle, sectionId, token) {
    return fetchSection(handle, sectionId).then(function (html) {
      if (token !== requestToken) return; // a newer swatch click won
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var incoming = doc.querySelector(SEL.productInfo);
      var current = document.querySelector(SEL.productInfo);
      if (!incoming || !current) throw new Error('product-info missing in section render');

      // Keep the swap URL-silent (documented Diesel parity): the swapped-in
      // section must not rewrite the address bar on later option changes.
      // updateURL() still refreshes share-button to the correct colour URL.
      incoming.setAttribute('data-update-url', 'false');

      // Match the no-default-size UX on the swapped-in colour: strip the
      // server-rendered default size selection BEFORE it's inserted, so the new
      // size grid paints unselected (no flash). Must edit ATTRIBUTES, not the
      // .checked property — viewTransition re-serializes via outerHTML, which
      // only preserves attributes. Single-value (one-size) options are left as is.
      Array.prototype.forEach.call(
        incoming.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown'),
        function (fs) {
          var radios = fs.querySelectorAll('input[type="radio"]');
          if (radios.length > 1) {
            Array.prototype.forEach.call(radios, function (r) { r.removeAttribute('checked'); });
          }
          var opts = fs.querySelectorAll('select option');
          if (opts.length > 1) {
            Array.prototype.forEach.call(fs.querySelectorAll('select option[selected]'), function (o) {
              o.removeAttribute('selected');
            });
          }
        }
      );

      // Dawn's own swap helper: dedupes ids, re-runs scripts, double-buffers.
      // It's a top-level `class` in global.js, so it's a global lexical
      // binding rather than a window property — check it bare.
      if (typeof HTMLUpdateUtility !== 'undefined' && HTMLUpdateUtility.viewTransition) {
        HTMLUpdateUtility.viewTransition(current, incoming, [preProcess], [postProcess]);
      } else {
        preProcess(incoming);
        current.replaceWith(incoming);
        postProcess();
      }

      // Let the sticky-info controller re-measure against the new DOM.
      window.dispatchEvent(new Event('resize'));
      document.dispatchEvent(new CustomEvent('dsl:swatch:swapped', { detail: { handle: handle } }));
    });
  }

  function onSwatchClick(e) {
    var btn = e.target.closest(SEL.swatch);
    if (!btn) return;
    e.preventDefault();
    var handle = btn.getAttribute('data-swatch-handle');
    if (!handle) return;
    if (btn.classList.contains('is-current')) return; // already showing

    setActive(btn);

    // This theme exposes the PDP section id on <product-info data-section>;
    // keep the generic fallbacks for portability.
    var sectionEl = document.querySelector('product-info[data-section], [data-section-type="product"], [data-section-id]');
    var sectionId = sectionEl && (sectionEl.getAttribute('data-section') || sectionEl.getAttribute('data-section-id'));

    var token = ++requestToken;

    var work = sectionId
      ? swapViaSection(handle, sectionId, token)
      : Promise.reject(new Error('no section id'));

    work.catch(function (err) {
      // Never fall back to a cosmetic-only swap: showing the new colour while
      // the form still holds the old one is exactly the cart bug this file
      // exists to prevent. Navigating always yields a correct page.
      console.warn('[dsl-swatch] swap failed, navigating:', err);
      window.location.href = '/products/' + handle;
    });
  }

  function init() {
    document.addEventListener('click', onSwatchClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
